import { useEffect } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import UnderlineExtension from "@tiptap/extension-underline";

import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  Highlighter,
  List,
  ListOrdered,
  Quote,
  Undo,
  Redo,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
} from "lucide-react";

const RichTextEditor = ({ content, value, onChange }) => {
  const resolvedContent = value ?? content ?? "";

  const editor = useEditor({
    extensions: [
      StarterKit,
      UnderlineExtension,
      Highlight.configure({ multicolor: true }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
    ],
    editorProps: {
      attributes: {
        class: "form-control border-1 min-vh-25 text-start",
        dir: "ltr",
        style: "text-align: left; direction: ltr;",
      },
    },
    content: resolvedContent,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
    immediatelyRender: false,
  });

  useEffect(() => {
    if (!editor) return;

    const currentHtml = editor.getHTML();
    if (currentHtml !== resolvedContent) {
      // Keep TipTap in sync when parent form data changes (e.g. edit mode).
      editor.commands.setContent(resolvedContent, false);
    }
  }, [editor, resolvedContent]);

  return (
    <div className="card p-3">
      {editor && <Toolbar editor={editor} />}
      <EditorContent editor={editor} />

      {/* Add Submit formData to the console for demonstration purposes. In a real application, you would send this data to your server or use it as needed. */}

      {/* <button
        className="btn btn-primary mt-3"
        onClick={() => {
          const formData = {
            content: editor.getHTML(),
          };
          console.log("Form Data:", formData);
        }}
      >
        Submit
      </button> */}
    </div>
  );
};

export default RichTextEditor;

function Toolbar({ editor }) {
  if (!editor) return null;
  const isActive = (name, attrs) => editor.isActive(name, attrs);
  const isAlignActive = (align) => editor.isActive({ textAlign: align });

  return (
    <div className="btn-toolbar mb-2" role="toolbar">
      <div className="btn-group me-2" role="group">
        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("bold") ? " active" : ""}`}
          onClick={() => editor.chain().focus().toggleBold().run()}
          title="Bold"
        >
          <Bold size={18} />
        </button>
        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("italic") ? " active" : ""}`}
          onClick={() => editor.chain().focus().toggleItalic().run()}
          title="Italic"
        >
          <Italic size={18} />
        </button>
        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("underline") ? " active" : ""}`}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          title="Underline"
        >
          <Underline size={18} />
        </button>
        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("strike") ? " active" : ""}`}
          onClick={() => editor.chain().focus().toggleStrike().run()}
          title="Strikethrough"
        >
          <Strikethrough size={18} />
        </button>
        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("highlight") ? " active" : ""}`}
          onClick={() =>
            editor.chain().focus().toggleHighlight({ color: "#fdeb80" }).run()
          }
          title="Highlight"
        >
          <Highlighter size={18} />
        </button>

        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isAlignActive("left") ? " active" : ""}`}
          onClick={() => editor.chain().focus().setTextAlign("left").run()}
          title="Align Left"
        >
          <AlignLeft size={18} />
        </button>

        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isAlignActive("center") ? " active" : ""}`}
          onClick={() => editor.chain().focus().setTextAlign("center").run()}
          title="Align Center"
        >
          <AlignCenter size={18} />
        </button>

        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isAlignActive("right") ? " active" : ""}`}
          onClick={() => editor.chain().focus().setTextAlign("right").run()}
          title="Align Right"
        >
          <AlignRight size={18} />
        </button>

        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isAlignActive("justify") ? " active" : ""}`}
          onClick={() => editor.chain().focus().setTextAlign("justify").run()}
          title="Align Justify"
        >
          <AlignJustify size={18} />
        </button>

        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("code") ? " active" : ""}`}
          onClick={() => editor.chain().focus().toggleCode().run()}
          title="Code"
        >
          <Code size={18} />
        </button>

        <div className="dropdown d-inline">
          <button
            className={`btn btn-outline-secondary btn-sm dropdown-toggle${isActive("paragraph") ? " active" : ""}`}
            type="button"
            id="headingDropdown"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title="Heading"
          >
            H
          </button>
          <ul className="dropdown-menu" aria-labelledby="headingDropdown">
            <li>
              <button
                type="button"
                className={`dropdown-item${isActive("paragraph") ? " active" : ""}`}
                onClick={() => editor.chain().focus().setParagraph().run()}
              >
                Normal
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`dropdown-item${isActive("heading", { level: 1 }) ? " active" : ""}`}
                onClick={() =>
                  editor.chain().focus().setHeading({ level: 1 }).run()
                }
              >
                Heading 1
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`dropdown-item${isActive("heading", { level: 2 }) ? " active" : ""}`}
                onClick={() =>
                  editor.chain().focus().setHeading({ level: 2 }).run()
                }
              >
                Heading 2
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`dropdown-item${isActive("heading", { level: 3 }) ? " active" : ""}`}
                onClick={() =>
                  editor.chain().focus().setHeading({ level: 3 }).run()
                }
              >
                Heading 3
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`dropdown-item${isActive("heading", { level: 4 }) ? " active" : ""}`}
                onClick={() =>
                  editor.chain().focus().setHeading({ level: 4 }).run()
                }
              >
                Heading 4
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`dropdown-item${isActive("heading", { level: 5 }) ? " active" : ""}`}
                onClick={() =>
                  editor.chain().focus().setHeading({ level: 5 }).run()
                }
              >
                Heading 5
              </button>
            </li>
            <li>
              <button
                type="button"
                className={`dropdown-item${isActive("heading", { level: 6 }) ? " active" : ""}`}
                onClick={() =>
                  editor.chain().focus().setHeading({ level: 6 }).run()
                }
              >
                Heading 6
              </button>
            </li>
          </ul>
        </div>
      </div>

      <div className="btn-group me-2" role="group">
        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("bulletList") ? " active" : ""}`}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          title="Bullet List"
        >
          <List size={18} />
        </button>
        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("orderedList") ? " active" : ""}`}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          title="Ordered List"
        >
          <ListOrdered size={18} />
        </button>
        <button
          type="button"
          className={`btn btn-outline-secondary btn-sm${isActive("blockquote") ? " active" : ""}`}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          title="Blockquote"
        >
          <Quote size={18} />
        </button>
      </div>

      <div className="btn-group me-2" role="group">
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo"
        >
          <Undo size={18} />
        </button>
        <button
          type="button"
          className="btn btn-outline-secondary btn-sm"
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo"
        >
          <Redo size={18} />
        </button>
      </div>
    </div>
  );
}

// BubbleMenu and FloatingMenu removed for Bootstrap simplification
